import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { getStoredSource } from "../lib/tracking";

const GOALS = [
  { value: "vendere", label: "Vendere un immobile" },
  { value: "comprare", label: "Comprare un immobile" },
  { value: "investire", label: "Investire in immobiliare" },
  { value: "ristrutturare", label: "Ristrutturare / costruire" },
  { value: "analizzare", label: "Analizzare un immobile" },
  { value: "altro", label: "Altro / non lo so ancora" },
];

const BUDGETS = [
  "Non lo so ancora",
  "Fino a 100.000 €",
  "100.000 – 300.000 €",
  "300.000 – 500.000 €",
  "500.000 – 1.000.000 €",
  "Oltre 1.000.000 €",
];

export default function LeadForm({ defaultGoal = "", sourcePage = "home", onSubmitted }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ goal: defaultGoal, budget: "", name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const stored = getStoredSource();
      await api.post("/leads", { ...form, ...stored, source_page: sourcePage });
      toast.success("Richiesta ricevuta.", {
        description: "La direzione A.C. HUB ti ricontatta al più presto. Di solito entro un giorno lavorativo.",
      });
      setForm({ goal: "", budget: "", name: "", phone: "", email: "", message: "" });
      setStep(1);
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const canContinue = form.goal !== "";

  return (
    <form onSubmit={submit} data-testid="lead-form" className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <span className="font-mono text-[11px] text-gold">{step === 1 ? "01" : "02"}</span>
        <div className="h-px flex-1 bg-[#c9a45c]/25">
          <motion.div
            className="h-px bg-gold"
            initial={false}
            animate={{ width: step === 1 ? "50%" : "100%" }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <span className="font-mono text-[11px] text-[#929292]">02</span>
      </div>

      {step === 1 ? (
        <motion.div initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="grid gap-5">
          <label className="grid gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#929292]">Qual è il tuo obiettivo?</span>
            <select className="field" value={form.goal} onChange={set("goal")} data-testid="lead-goal-select">
              <option value="">Seleziona un obiettivo…</option>
              {GOALS.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </label>
          <label className="grid gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#929292]">Di quanto parliamo, all'incirca?</span>
            <select className="field" value={form.budget} onChange={set("budget")} data-testid="lead-budget-select">
              <option value="">Seleziona un range…</option>
              {BUDGETS.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            data-testid="lead-continue-button"
            disabled={!canContinue}
            onClick={() => setStep(2)}
            className="btn btn-gold w-full disabled:cursor-not-allowed disabled:opacity-40"
          >
            Continua
            <ArrowRight size={14} />
          </button>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <input
              className="field"
              placeholder="Nome e cognome *"
              value={form.name}
              onChange={set("name")}
              required
              minLength={2}
              data-testid="lead-name-input"
            />
            <input
              className="field"
              placeholder="Telefono *"
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              required
              minLength={8}
              data-testid="lead-phone-input"
            />
          </div>
          <input
            className="field"
            placeholder="Email (facoltativa)"
            type="email"
            value={form.email}
            onChange={set("email")}
            data-testid="lead-email-input"
          />
          <textarea
            className="field min-h-[92px] resize-y"
            placeholder="Raccontaci in due righe il tuo obiettivo…"
            value={form.message}
            onChange={set("message")}
            data-testid="lead-message-input"
          />
          <p className="flex items-start gap-2 text-[10px] leading-relaxed text-[#929292]">
            <ShieldCheck size={14} className="mt-0.5 shrink-0 text-gold" />
            I tuoi dati restano tra noi: li legge solo la direzione A.C. HUB, mai nessun altro.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              data-testid="lead-back-button"
              onClick={() => setStep(1)}
              className="btn btn-outline !min-h-[46px] !px-4"
              aria-label="Torna al passo precedente"
            >
              <ArrowLeft size={14} />
            </button>
            <button type="submit" data-testid="lead-submit-button" disabled={sending} className="btn btn-gold flex-1 disabled:opacity-60">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              {sending ? "Invio in corso…" : "Invia la richiesta"}
            </button>
          </div>
        </motion.div>
      )}
    </form>
  );
}
